import { useCallback } from 'react';
import { useMutation } from '@apollo/client/react';

import { SuggestedChangeStatusEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import {
  SuggestedChangeCreateDocument,
  ISuggestedChangeCreateMutation,
} from 'src/graphql/actions/suggestChanges/suggestedChangeCreate.mutation';
import { SuggestedChangesDocument } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { writeNewSuggestChanges, suggestChangesVar } from 'src/graphql/client/cache';
import { useDepositsSuggestChangesSlice } from 'src/slices';

import { createRequiredContext } from '../createRequiredContext';

const [useDepositsSuggestChanges, DepositsSuggestChangesProvider] = createRequiredContext<
  {
    sendSuggestedChanges: () => Promise<ISuggestedChangeCreateMutation>;
  } & Omit<ReturnType<typeof useDepositsSuggestChangesSlice>, 'setInitialData'>
>();

type Props = {
  escrowId: string;
};

const DepositsSuggestChangesContextProvider = ({ escrowId, children }: React.PropsWithChildren<Props>) => {
  const [createSuggestedChanges] = useMutation(SuggestedChangeCreateDocument, {
    refetchQueries: [SuggestedChangesDocument],
  });
  const { allDeletedAssets, allDeletedCredentials, setInitialData, ...rest } = useDepositsSuggestChangesSlice();

  const sendSuggestedChanges = useCallback(async () => {
    const { data } = await createSuggestedChanges({
      variables: {
        escrowId,
        suggestedChangeParams: {
          depositSuggestedChange: {
            deletedAssets: allDeletedAssets.map(({ id }) => ({ id })),
            ...(Boolean(allDeletedCredentials.length) && {
              deletedSaasCredentials: allDeletedCredentials.map(({ id }) => ({ id })),
            }),
          },
        },
      },
    });

    if (!data?.suggestedChangeCreate?.suggestedChange) {
      const errorMsg = data?.suggestedChangeCreate?.errors?.[0] || 'Unknown error';
      throw new Error(errorMsg);
    }

    writeNewSuggestChanges(suggestChangesVar)('deposits', {
      id: data.suggestedChangeCreate.suggestedChange.id,
      creator: data.suggestedChangeCreate.suggestedChange.creatorType,
      status: data.suggestedChangeCreate.suggestedChange.partyStatus as SuggestedChangeStatusEnum,
      payload: data.suggestedChangeCreate.suggestedChange.payload,
      receiver: data.suggestedChangeCreate.suggestedChange.receiverType as EscrowPartyTypeEnum,
    });
    setInitialData();

    return data;
  }, [allDeletedAssets, allDeletedCredentials, createSuggestedChanges, escrowId]);

  const providerValue = {
    sendSuggestedChanges,
    allDeletedAssets,
    allDeletedCredentials,
    ...rest,
  };

  return <DepositsSuggestChangesProvider value={providerValue}>{children}</DepositsSuggestChangesProvider>;
};

export { useDepositsSuggestChanges, DepositsSuggestChangesContextProvider };
